import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  MdConfirmationNumber,
  MdEditNote,
  MdIosShare,
  MdPeople
} from 'react-icons/md';
import { IoMdTrash } from 'react-icons/io';
import { Modal as ModalAnt } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useRouter } from 'next/router';
import { useFlags } from 'launchdarkly-react-client-sdk';

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Divider from 'legacy/common/components/wysiwyg-form/Divider';
import { FLAGS } from 'legacy/common/constants/launchdarkly';
import { useDeleteEvent } from 'legacy/events/hooks';
import { EventForUser } from 'legacy/events/types';
import { SECTION_ID } from '../new/ManageGuestsSection';

type ManageEventProps = {
  event: EventForUser;
  onClickViewShareOptions: () => void;
  toggleManageEvent: () => void;
  visible: boolean;
  toggleCheckout: () => void;
};

const getManageEventOptions = (t) => [
  {
    condition: () => true,
    id: 'button-get-tickets',
    onClick:
      ({ toggleCheckout }) =>
      () =>
        toggleCheckout(),
    label: t('events:dashboard.manageEventModal.getTickets'),
    icon: <MdConfirmationNumber size={24} />
  },
  {
    condition: (event: EventForUser) => event.isOrganizer,
    id: 'button-edit-event',
    onClick:
      ({ eventId, router }) =>
      () => {
        router.push(`/events/${eventId}?view=edit`);
      },
    label: t('events:dashboard.manageEventModal.eventDetails'),
    icon: <MdEditNote size={24} />
  },
  {
    condition: () => true,
    id: 'button-manage-guests',
    onClick:
      ({ eventId, router }) =>
      () => {
        router.push(`/events/${eventId}/dashboard#${SECTION_ID}`);
      },
    label: t('events:dashboard.manageEventModal.manageGuests'),
    icon: <MdPeople size={24} />
  },
  {
    condition: (event: EventForUser) =>
      event.isOrganizer || event.isPhotographer,
    featureFlag: FLAGS.ENABLE_EVENT_BULK_PHOTO_UPLOAD,
    id: 'button-upload-moments',
    onClick:
      ({ eventId, router }) =>
      () => {
        router.push(`/events/${eventId}?view=upload-moments`);
      },
    label: t('events:dashboard.manageEventModal.uploadMoments'),
    icon: (
      <img src="/img/events/create/moments.svg" className="w-[24px] h-[24px]" />
    )
  }
];

const ManageEvent: React.FC<ManageEventProps> = ({
  event,
  onClickViewShareOptions,
  toggleManageEvent,
  visible,
  toggleCheckout
}) => {
  const { t } = useTranslation(['events']);
  const router = useRouter();
  const deleteEventCommand = useDeleteEvent();
  const flags = useFlags();

  const MANAGE_EVENT_OPTIONS = getManageEventOptions(t);

  const onOptionClick = (action) => () => {
    toggleManageEvent();
    action();
  };

  const onDeleteDraft = () => {
    toggleManageEvent();

    ModalAnt.confirm({
      title: t('events:dashboard.manageEventModal.deleteEventTitle'),
      icon: <ExclamationCircleOutlined />,
      content: event.title
        ? t(
            'events:dashboard.manageEventModal.deleteEventConfirmationWithTitle',
            { title: event.title }
          )
        : t('events:dashboard.manageEventModal.deleteEventConfirmation'),
      centered: true,
      okText: t('events:dashboard.manageEventModal.yes'),
      okType: 'danger',
      cancelText: t('events:dashboard.manageEventModal.no'),
      onOk() {
        deleteEventCommand.mutate(event._id, {
          onSuccess: () => {
            router.push('/host');
          }
        });
      }
    });
  };

  return (
    <Modal
      type={ModalType.SMALL}
      onClose={toggleManageEvent}
      autoHeight
      hideCloseButton
      visible={visible}
    >
      <div className="p-4 flex flex-col">
        <ul>
          {MANAGE_EVENT_OPTIONS.map((option) =>
            option.condition(event) &&
            (option.featureFlag ? flags[option.featureFlag] : true) ? (
              <li
                key={option.id}
                data-testid={option.id}
                className="font-normal text-base cursor-pointer py-4 flex align-center gap-2 text-neutral-600"
                onClick={onOptionClick(
                  option.onClick({
                    eventId: event._id,
                    router,
                    toggleCheckout
                  })
                )}
              >
                {option.icon}
                {option.label}
              </li>
            ) : null
          )}
        </ul>
        <Divider size={4} />
        {!(event.private && !event.isOrganizer) && (
          <>
            <div className="flex flex-col gap-2 mt-6">
              <Button
                type={ButtonType.Inverted}
                onClick={onOptionClick(onClickViewShareOptions)}
                icon={<MdIosShare size={20} />}
              >
                {t('events:dashboard.manageEventModal.shareEvent')}
              </Button>
            </div>
            <Divider size={6} />
          </>
        )}
        {event.isOrganizer && (
          <div className="mt-6">
            <Button
              type={ButtonType.Danger}
              icon={<IoMdTrash size={20} />}
              onClick={onDeleteDraft}
            >
              {t('events:dashboard.manageEventModal.deleteEvent')}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ManageEvent;
